import { useEffect, useState } from 'react';
import { getAnnouncements, saveAnnouncement, deleteAnnouncement, GetAnnouncementsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Megaphone, Pencil, Trash2, Loader2, MessageSquare } from 'lucide-react';

type Announcement = GetAnnouncementsOutputType['announcements'][0];

const ALL_ROLES = ['Trainee', 'Staff', 'Supervisor', 'Manager', 'Admin', 'Owner'];
const blankForm = { title: '', body: '', audience: 'All Staff', acknowledgementType: 'None', status: 'Active', sendSms: false, targetRoles: [] as string[] };

export default function AnnouncementsAdminTab() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...blankForm });
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    getAnnouncements({ adminView: true }).then(d => setAnnouncements(d.announcements)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm({ ...blankForm }); setDialogOpen(true); };
  const openEdit = (a: Announcement) => {
    setEditId(a.id);
    setForm({ title: a.title, body: a.body, audience: a.audience, acknowledgementType: a.acknowledgementType, status: a.status, sendSms: false, targetRoles: [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const res = await saveAnnouncement({ id: editId || undefined, ...form, targetRoles: form.targetRoles.length > 0 ? form.targetRoles : undefined });
      if (form.sendSms && (res as any).smsSentCount > 0) {
        toast.success(`Announcement saved · SMS sent to ${(res as any).smsSentCount} staff`);
      } else {
        toast.success(editId ? 'Announcement updated' : 'Announcement created');
      }
      setDialogOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteAnnouncement({ id }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const statusColor: Record<string, string> = {
    Active: 'bg-primary/10 text-primary',
    Completed: 'bg-muted text-muted-foreground',
    Draft: 'bg-muted text-muted-foreground',
    Archived: 'bg-muted text-muted-foreground',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Announcements</h2>
          <p className="text-xs text-muted-foreground">Broadcast messages with optional SMS alerts and read receipts</p>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground">
          <Plus className="w-4 h-4 mr-1.5" /> New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No announcements yet. Create your first to broadcast to staff.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Title</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase hidden md:table-cell">Audience</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase hidden md:table-cell">Ack</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Read</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {announcements.map(a => (
                <tr key={a.id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{a.title}</p>
                    <p className="text-xs text-muted-foreground">{a.postedByName} · {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{a.audience}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">{a.acknowledgementType}</td>
                  <td className="px-4 py-3 text-xs font-medium">
                    <span className={a.readCount >= a.totalTargeted && a.totalTargeted > 0 ? 'text-foreground' : 'text-primary'}>
                      {a.readCount}/{a.totalTargeted}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[a.status] || 'bg-muted text-muted-foreground'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(a)}><Pencil className="w-3 h-3" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{a.title}"?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(a.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Announcement' : 'New Announcement'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Announcement title" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Message Body</label>
              <Textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} placeholder="Write your announcement here..." className="min-h-24" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Audience</label>
                <Select value={form.audience} onValueChange={v => setForm(f => ({ ...f, audience: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All Staff">All Staff</SelectItem>
                    <SelectItem value="Front of House">Front of House</SelectItem>
                    <SelectItem value="Back of House">Back of House</SelectItem>
                    <SelectItem value="Kitchen">Kitchen</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Acknowledgement</label>
                <Select value={form.acknowledgementType} onValueChange={v => setForm(f => ({ ...f, acknowledgementType: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="None">None required</SelectItem>
                    <SelectItem value="Tick to Confirm">Tick to confirm</SelectItem>
                    <SelectItem value="Type Initials">Type initials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.sendSms}
                    onCheckedChange={v => setForm(f => ({ ...f, sendSms: v }))}
                    id="sms-toggle"
                  />
                  <label htmlFor="sms-toggle" className="text-sm text-foreground flex items-center gap-1 cursor-pointer">
                    <MessageSquare className="w-3.5 h-3.5 text-primary" /> Send SMS
                  </label>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Target Roles (optional — leave empty for all)</label>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES.map(r => (
                  <label key={r} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.targetRoles.includes(r)} onChange={e => {
                      setForm(f => ({ ...f, targetRoles: e.target.checked ? [...f.targetRoles, r] : f.targetRoles.filter(x => x !== r) }));
                    }} className="rounded" />
                    {r}
                  </label>
                ))}
              </div>
            </div>
            {form.sendSms && (
              <div className="bg-primary/5 border border-primary/20 rounded-md px-3 py-2 text-xs text-muted-foreground">
                An SMS will be sent to staff in the selected audience: "New announcement — log in to read and acknowledge"
              </div>
            )}
            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editId ? 'Update Announcement' : 'Post Announcement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
